import 'react';
import styled, { css } from 'styled-components';
import { f as fontSize, L as LineHeight, B as BreakpointMedia, _ as _objectSpread2, b as baseFontSize } from './index-c76be973.esm.js';
import { _ as _objectWithoutProperties } from './objectWithoutProperties-7e1dfdab.esm.js';
import { pxToRem, getThemeName, ThemeNames, filterGlobalProps } from '@lendi-ui/commons/utils';
import getTypography from '@lendi-ui/commons/typography';
import { jsx } from 'react/jsx-runtime';

var getFontSize = variant => {
  var styles = styleData[variant];
  return styles.fontSize;
};
var getLineHeight = variant => {
  var styles = styleData[variant];
  return styles.lineHeight;
};
var getTextColor = (theme, appearance) => {
  if (appearance) {
    if (appearance === 'error') {
      return theme.color.interaction.error;
    }
    if (appearance === 'subtle') {
      return theme.color.text.subtle;
    }
  }
  return theme.color.text.main;
};
var linkStyle = theme => {
  return {
    textDecoration: 'underline',
    color: theme.color.interaction.main,
    fontFamily: theme.asset.font.body.family,
    '&:hover': {
      textDecoration: 'none'
    }
  };
};
var styleData = {
  display: {
    fontSize: {
      mobile: pxToRem(36),
      desktop: pxToRem(fontSize[10])
    },
    lineHeight: 1.31
  },
  display1: {
    fontSize: {
      mobile: pxToRem(fontSize[9]),
      desktop: pxToRem(fontSize[10])
    },
    lineHeight: LineHeight.heading
  },
  display2: {
    hasMobile: true,
    fontSize: {
      mobile: pxToRem(fontSize[8]),
      desktop: pxToRem(fontSize[9])
    },
    lineHeight: LineHeight.heading
  },
  title1: {
    hasMobile: true,
    fontSize: {
      mobile: pxToRem(fontSize[7]),
      desktop: pxToRem(fontSize[8])
    },
    lineHeight: LineHeight.heading
  },
  title2: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[6])
    },
    lineHeight: LineHeight.heading
  },
  title3: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[5])
    },
    lineHeight: LineHeight.heading
  },
  title4: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[4])
    },
    lineHeight: LineHeight.heading
  },
  lead1: {
    hasMobile: true,
    fontSize: {
      mobile: pxToRem(fontSize[5]),
      desktop: pxToRem(fontSize[6])
    },
    lineHeight: LineHeight.body
  },
  lead2: {
    hasMobile: true,
    fontSize: {
      mobile: pxToRem(fontSize[3]),
      desktop: pxToRem(fontSize[5])
    },
    lineHeight: LineHeight.body
  },
  subtitle1: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[3])
    },
    lineHeight: LineHeight.body
  },
  subtitle2: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[2])
    },
    lineHeight: LineHeight.body
  },
  body1: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[3])
    },
    lineHeight: LineHeight.body
  },
  body2: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[2])
    },
    lineHeight: LineHeight.body
  },
  detail1: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[1])
    },
    lineHeight: LineHeight.body
  },
  detail2: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[1])
    },
    lineHeight: LineHeight.body
  },
  overline: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[2])
    },
    lineHeight: LineHeight.heading
  },
  button1: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[3])
    },
    lineHeight: LineHeight.body
  },
  button2: {
    hasMobile: false,
    fontSize: {
      mobile: pxToRem(fontSize[2])
    },
    lineHeight: LineHeight.body
  }
};

var _excluded$1 = ["children", "variant", "className"];
var getThemeTextAlignment = themeName => {
  if (themeName === ThemeNames.Lendi) {
    return 'center';
  }
  return 'left';
};
var checkIsHeading = variant => {
  var headingsVariants = ['display', 'display1', 'display2', 'title1', 'title2', 'title3', 'title4'];
  return headingsVariants.includes(variant);
};
var Component = styled.p.withConfig({
  displayName: "Text__Component",
  componentId: "lui__sc-xe5utb-0"
})(_ref => {
  var _theme$name;
  var {
    variant,
    theme,
    align,
    isMobile,
    styleTarget,
    $display,
    appearance,
    isInverse
  } = _ref;
  var fontSizeValue = getFontSize(variant);
  var isHeading = checkIsHeading(variant);
  var hasDesktopFontSize = Object.prototype.hasOwnProperty.call(fontSizeValue, 'desktop');
  var themeName = (_theme$name = theme === null || theme === void 0 ? void 0 : theme.name) !== null && _theme$name !== void 0 ? _theme$name : getThemeName(); // fallback to old getTheme function when latest Theme not being used. To be removed in next major

  var styleObj = {
    margin: 0,
    display: $display !== null && $display !== void 0 ? $display : 'block',
    fontFamily: isHeading ? theme.asset.font.heading.family : theme.asset.font.body.family,
    fontWeight: getTypography(variant, 'fontWeight'),
    textAlign: align !== null && align !== void 0 ? align : getThemeTextAlignment(themeName),
    color: isInverse ? theme.color.text.inverse : getTextColor(theme, appearance),
    fontSize: fontSizeValue.mobile,
    lineHeight: getLineHeight(variant),
    a: linkStyle(theme)
  };
  if (variant === 'overline') {
    styleObj = Object.assign({
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }, styleObj);
  }
  if (hasDesktopFontSize && !isMobile) {
    styleObj = Object.assign({
      [BreakpointMedia.sm]: {
        fontSize: fontSizeValue.desktop
      }
    }, styleObj);
  }
  if (styleTarget) {
    return {
      [styleTarget]: styleObj
    };
  }
  return styleObj;
});
var Text = _ref2 => {
  var {
      children,
      variant = 'body1',
      className
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded$1);
  var globalProps = filterGlobalProps(props);
  var textVariantClass;
  switch (variant) {
    case 'display':
      textVariantClass = 'lui-display';
      break;
    case 'display1':
      textVariantClass = 'lui-display1';
      break;
    case 'display2':
      textVariantClass = 'lui-display2';
      break;
    case 'title1':
      textVariantClass = 'lui-title1';
      break;
    case 'title2':
      textVariantClass = 'lui-title2';
      break;
    case 'title3':
      textVariantClass = 'lui-title3';
      break;
    case 'title4':
      textVariantClass = 'lui-title4';
      break;
    case 'lead1':
      textVariantClass = 'lui-lead1';
      break;
    case 'lead2':
      textVariantClass = 'lui-lead2';
      break;
    case 'subtitle1':
      textVariantClass = 'lui-subtitle1';
      break;
    case 'subtitle2':
      textVariantClass = 'lui-subtitle2';
      break;
    case 'body1':
      textVariantClass = 'lui-body1';
      break;
    case 'body2':
      textVariantClass = 'lui-body2';
      break;
    case 'button1':
      textVariantClass = 'lui-button1';
      break;
    case 'button2':
      textVariantClass = 'lui-button2';
      break;
    case 'detail1':
      textVariantClass = 'lui-detail1';
      break;
    case 'detail2':
      textVariantClass = 'lui-detail2';
      break;
    case 'overline':
      textVariantClass = 'lui-overline';
      break;
    default:
      textVariantClass = 'lui-body1';
  }
  return /*#__PURE__*/jsx(Component, _objectSpread2(_objectSpread2({
    id: props.id,
    className: className ? "".concat(className, " ").concat(textVariantClass) : textVariantClass,
    as: props.tag,
    variant: variant,
    align: props.align,
    appearance: props.appearance,
    isMobile: props.isMobile,
    htmlFor: props.for,
    $display: props.display,
    role: props.role,
    isInverse: props.isInverse
  }, globalProps), {}, {
    children: children
  }));
};

var Paragraph = styled(Text).withConfig({
  displayName: "Paragraph",
  componentId: "lui__sc-iqzapc-0"
})(_ref => {
  var {
    theme
  } = _ref;
  return {
    a: linkStyle(theme)
  };
});
var P = _ref2 => {
  var {
    appearance,
    variant,
    children
  } = _ref2;
  return /*#__PURE__*/jsx(Paragraph, {
    variant: variant !== null && variant !== void 0 ? variant : 'body1',
    tag: "p",
    appearance: appearance,
    children: children
  });
};

var A = styled.a.withConfig({
  displayName: "Link__A",
  componentId: "lui__sc-1u7drzy-0"
})(["", ""], _ref => {
  var {
    theme
  } = _ref;
  return linkStyle(theme);
});

var _excluded = ["children", "textSize"];
var StyledRichText = styled.div.withConfig({
  displayName: "RichText__StyledRichText",
  componentId: "lui__sc-nganh6-0"
})(["font-size:", ";line-height:", ";color:", ";", "{font-size:", ";}> *{font-family:", ";font-weight:", ";font-size:1em;margin-bottom:0;margin-top:0;}> * + *{margin-top:", ";", "{margin-top:", ";}}> * li{margin-top:0.5em;}> * + h1,> * + h2,> * + div,> div + *{margin-top:", ";}> h1 + *,> h2 + *{margin-top:", ";}> h3 + *,> h4 + *{margin-top:", ";}> * + h3,> * + h4{margin-top:", ";}> h1,> h2,> h3,> h4{font-family:", ";line-height:", ";font-weight:", ";color:", ";}> h1{font-size:", ";", ";}> h2{font-size:", ";", ";}> h3{font-size:", ";}> h4{font-size:", ";}> a,li > a,p > a{", ";}"], pxToRem(baseFontSize), styleData.body1.lineHeight, _ref => {
  var {
    theme
  } = _ref;
  return theme.color.text.subtle;
}, BreakpointMedia.sm, _ref2 => {
  var {
    textSize
  } = _ref2;
  return textSize === 'lg' && pxToRem(fontSize[5]);
}, _ref3 => {
  var {
    theme
  } = _ref3;
  return theme.asset.font.body.family;
}, _ref4 => {
  var {
    theme
  } = _ref4;
  return theme.asset.font.body.weight;
}, pxToRem(fontSize[3]), BreakpointMedia.sm, _ref5 => {
  var {
    textSize
  } = _ref5;
  return textSize === 'lg' && pxToRem(fontSize[5]);
}, pxToRem(fontSize[10]), pxToRem(fontSize[6]), pxToRem(fontSize[5]), pxToRem(fontSize[9]), _ref6 => {
  var {
    theme
  } = _ref6;
  return theme.asset.font.body.family;
}, LineHeight.heading, _ref7 => {
  var {
    theme
  } = _ref7;
  return theme.asset.font.heading.weight;
}, _ref8 => {
  var {
    theme
  } = _ref8;
  return theme.color.text.main;
}, styleData.display2.fontSize.mobile, 'desktop' in styleData.display2.fontSize && css(["", "{font-size:", ";}"], BreakpointMedia.sm, styleData.display2.fontSize.desktop), styleData.title1.fontSize.mobile, 'desktop' in styleData.title1.fontSize && css(["", "{font-size:", ";}"], BreakpointMedia.sm, styleData.title1.fontSize.desktop), styleData.title2.fontSize.mobile, styleData.title3.fontSize.mobile, _ref9 => {
  var {
    theme
  } = _ref9;
  return linkStyle(theme);
});

// Direct children have margin removed and are spaced out xs
var RichText = _ref10 => {
  var {
      children,
      textSize = 'md'
    } = _ref10,
    props = _objectWithoutProperties(_ref10, _excluded);
  return /*#__PURE__*/jsx(StyledRichText, _objectSpread2(_objectSpread2({
    textSize: textSize
  }, props), {}, {
    children: children
  }));
};

export { A, P, RichText as R, Text as T };
